import {
  createUuid,
  MemorisationStateCreateSchema,
  MemorisationStateSchema,
  MemorisationStateUpdateSchema,
  type CreateMemorisationStateInput,
  type MemorisationState,
  type UpdateMemorisationStateInput,
} from "../../domain";
import type { DuaWorkspaceDatabase } from "../database";
import { EntityNotFoundError, parseEntityId, type RepositoryOptions } from "./repository-utils";

export class MemorisationRepository {
  readonly #database: DuaWorkspaceDatabase;
  readonly #now: () => string;
  readonly #createId: () => string;

  constructor(database: DuaWorkspaceDatabase, options: RepositoryOptions = {}) {
    this.#database = database;
    this.#now = options.now ?? (() => new Date().toISOString());
    this.#createId = options.createId ?? createUuid;
  }

  async list(includeDeleted = false): Promise<MemorisationState[]> {
    let records = await this.#database.memorisationStates.toArray();
    if (!includeDeleted) {
      records = records.filter((record) => record.deletedAt === undefined);
    }
    return records
      .map((record) => MemorisationStateSchema.parse(record))
      .sort((left, right) =>
        (left.nextReviewAt ?? "9999").localeCompare(right.nextReviewAt ?? "9999"),
      );
  }

  async listDue(at: string = this.#now()): Promise<MemorisationState[]> {
    const records = await this.#database.memorisationStates.where("nextReviewAt").belowOrEqual(at).toArray();
    return records
      .filter((record) => record.deletedAt === undefined)
      .map((record) => MemorisationStateSchema.parse(record));
  }

  async getById(id: string, includeDeleted = false): Promise<MemorisationState | undefined> {
    const record = await this.#database.memorisationStates.get(parseEntityId(id));
    if (record === undefined || (!includeDeleted && record.deletedAt !== undefined)) {
      return undefined;
    }
    return MemorisationStateSchema.parse(record);
  }

  async getByTarget(
    targetType: MemorisationState["targetType"],
    targetId: string,
  ): Promise<MemorisationState | undefined> {
    const record = await this.#database.memorisationStates
      .where("[targetType+targetId]")
      .equals([targetType, targetId])
      .first();
    if (record === undefined || record.deletedAt !== undefined) {
      return undefined;
    }
    return MemorisationStateSchema.parse(record);
  }

  async create(input: CreateMemorisationStateInput): Promise<MemorisationState> {
    const values = MemorisationStateCreateSchema.parse(input);
    const timestamp = this.#now();
    const record = MemorisationStateSchema.parse({
      id: this.#createId(),
      ...values,
      createdAt: timestamp,
      updatedAt: timestamp,
    });
    return this.#database.transaction(
      "rw",
      [this.#database.memorisationStates, this.#database.personalDuas],
      async () => {
        await this.#assertPersonalDuaTarget(values.targetType, values.targetId);
        await this.#database.memorisationStates.add(record);
        return record;
      },
    );
  }

  async upsertForTarget(input: CreateMemorisationStateInput): Promise<MemorisationState> {
    const values = MemorisationStateCreateSchema.parse(input);
    return this.#database.transaction(
      "rw",
      [this.#database.memorisationStates, this.#database.personalDuas],
      async () => {
        await this.#assertPersonalDuaTarget(values.targetType, values.targetId);
        const existing = await this.#database.memorisationStates
          .where("[targetType+targetId]")
          .equals([values.targetType, values.targetId])
          .first();
        const timestamp = this.#now();
        const upserted = MemorisationStateSchema.parse({
          id: existing?.id ?? this.#createId(),
          ...values,
          createdAt: existing?.createdAt ?? timestamp,
          updatedAt: timestamp,
        });
        await this.#database.memorisationStates.put(upserted);
        return upserted;
      },
    );
  }

  async update(id: string, input: UpdateMemorisationStateInput): Promise<MemorisationState> {
    const entityId = parseEntityId(id);
    const patch = MemorisationStateUpdateSchema.parse(input);
    return this.#database.transaction("rw", this.#database.memorisationStates, async () => {
      const current = await this.#database.memorisationStates.get(entityId);
      if (current === undefined || current.deletedAt !== undefined) {
        throw new EntityNotFoundError("MemorisationState", entityId);
      }
      const updated = MemorisationStateSchema.parse({ ...current, ...patch, updatedAt: this.#now() });
      await this.#database.memorisationStates.put(updated);
      return updated;
    });
  }

  async softDelete(id: string): Promise<MemorisationState> {
    const entityId = parseEntityId(id);
    return this.#database.transaction("rw", this.#database.memorisationStates, async () => {
      const current = await this.#database.memorisationStates.get(entityId);
      if (current === undefined) {
        throw new EntityNotFoundError("MemorisationState", entityId);
      }
      if (current.deletedAt !== undefined) {
        return MemorisationStateSchema.parse(current);
      }
      const timestamp = this.#now();
      const deleted = MemorisationStateSchema.parse({ ...current, deletedAt: timestamp, updatedAt: timestamp });
      await this.#database.memorisationStates.put(deleted);
      return deleted;
    });
  }

  async restore(id: string): Promise<MemorisationState> {
    const entityId = parseEntityId(id);
    return this.#database.transaction("rw", this.#database.memorisationStates, async () => {
      const current = await this.#database.memorisationStates.get(entityId);
      if (current === undefined) {
        throw new EntityNotFoundError("MemorisationState", entityId);
      }
      const candidate = { ...current };
      delete candidate.deletedAt;
      const restored = MemorisationStateSchema.parse({ ...candidate, updatedAt: this.#now() });
      await this.#database.memorisationStates.put(restored);
      return restored;
    });
  }

  async #assertPersonalDuaTarget(
    targetType: MemorisationState["targetType"],
    targetId: string,
  ): Promise<void> {
    if (targetType !== "personal_dua") return;
    const dua = await this.#database.personalDuas.get(targetId);
    if (dua === undefined || dua.deletedAt !== undefined) {
      throw new EntityNotFoundError("PersonalDua", targetId);
    }
  }
}
